import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

interface WoodType {
  name: string;
  hardness: string;
  color: string;
  usage: string;
  icon: string;
}

interface JointType {
  name: string;
  difficulty: string;
  feature: string;
  icon: string;
  color: string;
}

interface WoodTool {
  name: string;
  purpose: string;
  era: string;
  country: string;
}

interface JointDiagramPart {
  label: string;
  x: number;
  y: number;
}

interface WoodworkingCraftTemplateProps {
  topic: string;
  subtitle: string;
  woodTypes: WoodType[];
  jointTypes: JointType[];
  tools: WoodTool[];
  jointDiagram: JointDiagramPart[];
  techniques: { label: string; icon: string }[];
  stats: { value: string; label: string }[];
  bottomQuote: string;
}

const DEFAULT_PROPS: WoodworkingCraftTemplateProps = {
  topic: '木工与榫卯技艺',
  subtitle: 'Woodworking & Joinery',
  woodTypes: [
    {
      name: '红木',
      hardness: '极硬',
      color: '#991b1b',
      usage: '明清家具、工艺品',
      icon: '🪵',
    },
    {
      name: '橡木',
      hardness: '硬',
      color: '#a16207',
      usage: '家具、地板、酒桶',
      icon: '🌳',
    },
    {
      name: '胡桃木',
      hardness: '中硬',
      color: '#78350f',
      usage: '高端家具、枪托',
      icon: '🥜',
    },
    {
      name: '樱桃木',
      hardness: '中等',
      color: '#be123c',
      usage: '橱柜、乐器',
      icon: '🍒',
    },
  ],
  jointTypes: [
    {
      name: '燕尾榫',
      difficulty: '★★★★',
      feature: '抗拉力极强，角部连接',
      icon: '🔗',
      color: '#d97706',
    },
    {
      name: '明榫',
      difficulty: '★★★',
      feature: '穿透可见，结构坚固',
      icon: '📌',
      color: '#b45309',
    },
    {
      name: '暗榫',
      difficulty: '★★★★★',
      feature: '隐蔽美观，大师工艺',
      icon: '🔒',
      color: '#92400e',
    },
    {
      name: '粽角榫',
      difficulty: '★★★★★',
      feature: '三方交汇，皇家家具',
      icon: '👑',
      color: '#78350f',
    },
  ],
  tools: [
    { name: '框锯', purpose: '开料断料', era: '春秋', country: '🇨🇳' },
    { name: '刨子', purpose: '平面光滑处理', era: '汉代', country: '🇨🇳' },
    { name: '凿子', purpose: '开榫眼、雕刻', era: '商代', country: '🇨🇳' },
    { name: '木槌', purpose: '装配敲击', era: '新石器', country: '🇨🇳' },
  ],
  jointDiagram: [
    { label: '榫头', x: 95, y: 75 },
    { label: '榫眼', x: 295, y: 75 },
    { label: '肩部', x: 190, y: 165 },
    { label: '紧配合', x: 200, y: 38 },
  ],
  techniques: [
    { label: '弹墨线', icon: '📏' },
    { label: '开榫头', icon: '🪚' },
    { label: '凿榫眼', icon: '🔨' },
    { label: '试装配', icon: '🧩' },
  ],
  stats: [
    { value: '7000年', label: '中国木工史(河姆渡)' },
    { value: '33种', label: '鲁班锁基本变化' },
    { value: '0胶0钉', label: '传统榫卯纯木连接' },
  ],
  bottomQuote:
    '榫卯之间，藏的是匠人对木性的理解——不用一钉一胶，却能传承千年不朽',
};

export const WoodworkingCraftTemplate: React.FC<
  Partial<WoodworkingCraftTemplateProps>
> = (props) => {
  const p = { ...DEFAULT_PROPS, ...props };
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const headerIn = spring({
    frame,
    fps,
    config: { damping: 18, stiffness: 80 },
  });

  const woodStart = 30;
  const jointStart = 150;
  const diagramStart = 200;
  const toolStart = 300;
  const techStart = 380;
  const statsStart = 440;
  const quoteStart = 500;

  const quoteOpacity = interpolate(frame, [quoteStart, quoteStart + 30], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  const diagramDraw = interpolate(
    frame,
    [diagramStart, diagramStart + 100],
    [0, 1],
    { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
  );

  return (
    <AbsoluteFill
      style={{
        background: 'linear-gradient(135deg, #1a120b 0%, #27190e 40%, #120a05 100%)',
        fontFamily: '-apple-system, "PingFang SC", "Noto Sans SC", sans-serif',
        color: 'white',
        overflow: 'hidden',
        width: 1920,
        height: 1080,
      }}
    >
      {/* Floating wood grain particles */}
      {Array.from({ length: 50 }).map((_, i) => {
        const x = (i * 317) % 1920;
        const y = (i * 167) % 1080;
        const size = 2 + (i % 5);
        const drift = Math.sin(frame * 0.02 + i) * 6;
        const pulse = 0.05 + 0.04 * Math.sin(frame * 0.035 + i * 0.7);
        const colors = ['#d97706', '#b45309', '#92400e', '#fbbf24'];
        const isGrain = i % 4 === 0;
        return (
          <div
            key={`p-${i}`}
            style={{
              position: 'absolute',
              left: x,
              top: y + drift,
              width: isGrain ? size * 3 : size,
              height: isGrain ? 1.5 : size,
              borderRadius: isGrain ? 1 : '50%',
              background: colors[i % colors.length],
              opacity: pulse,
              transform: isGrain ? `rotate(${(i * 23) % 180}deg)` : undefined,
              boxShadow: `0 0 ${size * 2}px ${colors[i % colors.length]}30`,
            }}
          />
        );
      })}

      {/* ===== Header ===== */}
      <div
        style={{
          position: 'absolute',
          top: 28,
          left: 44,
          right: 44,
          opacity: headerIn,
          transform: `translateY(${(1 - headerIn) * 20}px)`,
          display: 'flex',
          alignItems: 'center',
          gap: 20,
        }}
      >
        <div
          style={{
            padding: '6px 18px',
            background: '#92400e',
            borderRadius: 8,
            fontSize: 24,
            fontWeight: 700,
            color: '#fef3c7',
            flexShrink: 0,
          }}
        >
          🪵 传统技艺
        </div>
        <h1
          style={{
            fontSize: 46,
            fontWeight: 900,
            margin: 0,
            color: '#fef3c7',
            whiteSpace: 'nowrap',
          }}
        >
          {p.topic}
        </h1>
        <p
          style={{
            fontSize: 26,
            color: '#a8a29e',
            margin: 0,
            whiteSpace: 'nowrap',
          }}
        >
          {p.subtitle}
        </p>
      </div>

      {/* ===== LEFT COLUMN: Wood Types ===== */}
      <div style={{ position: 'absolute', top: 120, left: 80, width: 420 }}>
        <div
          style={{
            fontSize: 26,
            color: '#a8a29e',
            marginBottom: 10,
            letterSpacing: '0.08em',
          }}
        >
          常用木材
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {p.woodTypes.map((w, i) => {
            const s = spring({
              frame: frame - (woodStart + i * 18),
              fps,
              config: { damping: 14, stiffness: 120 },
            });
            return (
              <div
                key={i}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 14,
                  background: '#1a120b',
                  borderRadius: 14,
                  padding: '12px 16px',
                  border: `1.5px solid ${w.color}40`,
                  opacity: s,
                  transform: `translateX(${(1 - s) * 30}px)`,
                }}
              >
                <div
                  style={{
                    width: 52,
                    height: 52,
                    borderRadius: '50%',
                    background: `${w.color}25`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 30,
                    flexShrink: 0,
                    boxShadow: `0 0 18px ${w.color}30`,
                  }}
                >
                  {w.icon}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
                    <span
                      style={{ fontSize: 22, fontWeight: 800, color: '#d97706' }}
                    >
                      {w.name}
                    </span>
                    <span style={{ fontSize: 20, color: '#78716c' }}>
                      硬度: {w.hardness}
                    </span>
                  </div>
                  <div
                    style={{ fontSize: 20, color: '#a8a29e', marginTop: 2 }}
                  >
                    用途: {w.usage}
                  </div>
                </div>
                <div
                  style={{
                    width: 20,
                    height: 20,
                    borderRadius: 4,
                    background: w.color,
                    border: '2px solid #fef3c720',
                    flexShrink: 0,
                  }}
                />
              </div>
            );
          })}
        </div>

        {/* Mortise-Tenon Joint Diagram */}
        <div style={{ marginTop: 24 }}>
          <div
            style={{
              fontSize: 24,
              color: '#a8a29e',
              marginBottom: 10,
              letterSpacing: '0.08em',
            }}
          >
            榫卯结构图解
          </div>
          <svg width="440" height="280" viewBox="0 0 440 280">
            {/* Tenon piece (left) */}
            <g
              opacity={diagramDraw}
              transform={`translate(${(1 - diagramDraw) * -40}, 0)`}
            >
              <rect
                x="15"
                y="55"
                width="155"
                height="65"
                rx="4"
                fill="#92400e"
                stroke="#d9770680"
                strokeWidth="2.5"
              />
              <rect
                x="170"
                y="68"
                width="60"
                height="38"
                rx="3"
                fill="#b45309"
                stroke="#d9770680"
                strokeWidth="2.5"
              />
              {[72, 85, 98].map((y) => (
                <line
                  key={y}
                  x1="28"
                  y1={y}
                  x2="158"
                  y2={y}
                  stroke="#d9770630"
                  strokeWidth="1.2"
                />
              ))}
            </g>

            {/* Mortise piece (right) */}
            <g
              opacity={diagramDraw}
              transform={`translate(${(1 - diagramDraw) * 40}, 0)`}
            >
              <rect
                x="252"
                y="35"
                width="170"
                height="105"
                rx="4"
                fill="#78350f"
                stroke="#d9770680"
                strokeWidth="2.5"
              />
              <rect
                x="252"
                y="62"
                width="68"
                height="44"
                rx="3"
                fill="#1a120b"
                stroke="#d9770650"
                strokeWidth="2"
                strokeDasharray="5 3"
              />
              {[52, 70, 88, 106, 122].map((y) => (
                <line
                  key={y}
                  x1="330"
                  y1={y}
                  x2="410"
                  y2={y}
                  stroke="#d9770620"
                  strokeWidth="1.2"
                />
              ))}
            </g>

            {/* Labels */}
            {p.jointDiagram.map((part, i) => {
              const labelS = spring({
                frame: frame - (diagramStart + 40 + i * 12),
                fps,
                config: { damping: 14, stiffness: 120 },
              });
              return (
                <g key={i} opacity={labelS}>
                  <text
                    x={part.x}
                    y={part.y}
                    fill="#fef3c7"
                    fontSize="18"
                    fontWeight="700"
                    textAnchor="middle"
                  >
                    {part.label}
                  </text>
                  <line
                    x1={part.x}
                    y1={part.y + 6}
                    x2={part.x}
                    y2={part.y + 18}
                    stroke="#d97706"
                    strokeWidth="1.5"
                    opacity={0.6}
                  />
                </g>
              );
            })}

            {/* Assembly arrow */}
            <g
              opacity={interpolate(
                frame,
                [diagramStart + 70, diagramStart + 95],
                [0, 1],
                { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
              )}
            >
              <line
                x1="185"
                y1="200"
                x2="245"
                y2="200"
                stroke="#d97706"
                strokeWidth="2.5"
              />
              <polygon points="242,194 254,200 242,206" fill="#d97706" />
              <text
                x="215"
                y="228"
                fill="#a8a29e"
                fontSize="17"
                fontWeight="600"
                textAnchor="middle"
              >
                嵌入装配
              </text>
            </g>
          </svg>
        </div>
      </div>

      {/* ===== CENTER: Joint Types ===== */}
      <div style={{ position: 'absolute', top: 120, left: 680, width: 600 }}>
        <div
          style={{
            fontSize: 26,
            color: '#a8a29e',
            marginBottom: 10,
            letterSpacing: '0.08em',
          }}
        >
          榫卯类型
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {p.jointTypes.map((jt, i) => {
            const s = spring({
              frame: frame - (jointStart + i * 18),
              fps,
              config: { damping: 14, stiffness: 120 },
            });
            return (
              <div
                key={i}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 14,
                  background: '#1a120b',
                  borderRadius: 14,
                  padding: '12px 16px',
                  border: `1.5px solid ${jt.color}30`,
                  opacity: s,
                  transform: `translateX(${(1 - s) * 30}px)`,
                }}
              >
                <div
                  style={{
                    width: 52,
                    height: 52,
                    borderRadius: '50%',
                    background: `${jt.color}20`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 30,
                    flexShrink: 0,
                    boxShadow: `0 0 18px ${jt.color}30`,
                  }}
                >
                  {jt.icon}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
                    <span
                      style={{ fontSize: 22, fontWeight: 800, color: jt.color }}
                    >
                      {jt.name}
                    </span>
                    <span style={{ fontSize: 20, color: '#78716c' }}>
                      难度: {jt.difficulty}
                    </span>
                  </div>
                  <div
                    style={{ fontSize: 20, color: '#a8a29e', marginTop: 2 }}
                  >
                    {jt.feature}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Woodworking Process Timeline */}
        <div style={{ marginTop: 24 }}>
          <div
            style={{
              fontSize: 26,
              color: '#a8a29e',
              marginBottom: 10,
              letterSpacing: '0.08em',
            }}
          >
            制作工序
          </div>
          <svg width="600" height="170" viewBox="0 0 600 170">
            {['选材', '画线弹墨', '锯切开料', '凿削成形', '试装打磨', '组装完成'].map(
              (step, i) => {
                const s = spring({
                  frame: frame - (diagramStart + 60 + i * 12),
                  fps,
                  config: { damping: 14, stiffness: 120 },
                });
                const cx = 52 + i * 100;
                return (
                  <g key={i} opacity={s}>
                    <circle
                      cx={cx}
                      cy="55"
                      r="30"
                      fill="#92400e30"
                      stroke="#d97706"
                      strokeWidth="2.5"
                    />
                    <text
                      x={cx}
                      y="61"
                      fill="#fef3c7"
                      fontSize="19"
                      fontWeight="700"
                      textAnchor="middle"
                    >
                      {step.slice(0, 2)}
                    </text>
                    {step.length > 2 && (
                      <text
                        x={cx}
                        y="110"
                        fill="#a8a29e"
                        fontSize="18"
                        fontWeight="600"
                        textAnchor="middle"
                      >
                        {step}
                      </text>
                    )}
                    {i < 5 && (
                      <line
                        x1={cx + 33}
                        y1="55"
                        x2={cx + 67}
                        y2="55"
                        stroke="#d97706"
                        strokeWidth="2"
                        strokeDasharray="5 3"
                        opacity={0.5}
                      />
                    )}
                  </g>
                );
              },
            )}
          </svg>
        </div>
      </div>

      {/* ===== RIGHT COLUMN: Essential Tools + Techniques ===== */}
      <div style={{ position: 'absolute', top: 120, right: 80, width: 380 }}>
        <div
          style={{
            fontSize: 26,
            color: '#a8a29e',
            marginBottom: 10,
            letterSpacing: '0.08em',
          }}
        >
          传统工具
        </div>
        {p.tools.map((t, i) => {
          const s = spring({
            frame: frame - (toolStart + i * 16),
            fps,
            config: { damping: 14, stiffness: 120 },
          });
          return (
            <div
              key={i}
              style={{
                background: '#1a120b',
                borderRadius: 12,
                padding: '12px 16px',
                border: '1.5px solid #44280f',
                marginBottom: 8,
                opacity: s,
                transform: `translateY(${(1 - s) * 16}px)`,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 24 }}>{t.country}</span>
                <span style={{ fontSize: 22, fontWeight: 800, color: '#fbbf24' }}>
                  {t.name}
                </span>
              </div>
              <div style={{ display: 'flex', gap: 16, marginTop: 4 }}>
                <span style={{ fontSize: 20, color: '#d97706', fontWeight: 700 }}>
                  {t.purpose}
                </span>
                <span style={{ fontSize: 20, color: '#78716c' }}>{t.era}时期</span>
              </div>
            </div>
          );
        })}

        {/* Techniques */}
        <div style={{ marginTop: 20 }}>
          <div
            style={{
              fontSize: 26,
              color: '#a8a29e',
              marginBottom: 10,
              letterSpacing: '0.08em',
            }}
          >
            基础技法
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 8,
            }}
          >
            {p.techniques.map((tech, i) => {
              const s = spring({
                frame: frame - (techStart + i * 12),
                fps,
                config: { damping: 14, stiffness: 130 },
              });
              return (
                <div
                  key={i}
                  style={{
                    background: '#1a120b',
                    borderRadius: 12,
                    padding: '14px 10px',
                    textAlign: 'center',
                    border: '1px solid #44280f',
                    opacity: s,
                    transform: `scale(${0.8 + 0.2 * s})`,
                  }}
                >
                  <div style={{ fontSize: 26, marginBottom: 4 }}>{tech.icon}</div>
                  <div
                    style={{ fontSize: 20, fontWeight: 700, color: '#fbbf24' }}
                  >
                    {tech.label}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ===== Stats (bottom bar) ===== */}
      <div
        style={{
          position: 'absolute',
          bottom: 105,
          left: 36,
          right: 36,
          display: 'flex',
          gap: 20,
        }}
      >
        {p.stats.map((st, i) => {
          const stIn = spring({
            frame: frame - (statsStart + i * 14),
            fps,
            config: { damping: 14, stiffness: 100 },
          });
          return (
            <div
              key={i}
              style={{
                flex: 1,
                background: '#1a120b',
                borderRadius: 14,
                padding: '14px 10px',
                textAlign: 'center',
                border: '1px solid #44280f',
                opacity: stIn,
                transform: `scale(${0.8 + 0.2 * stIn})`,
              }}
            >
              <div
                style={{ fontSize: 30, fontWeight: 900, color: '#d97706' }}
              >
                {st.value}
              </div>
              <div style={{ fontSize: 22, color: '#a8a29e', marginTop: 4 }}>
                {st.label}
              </div>
            </div>
          );
        })}
      </div>

      {/* ===== Bottom Quote ===== */}
      <div
        style={{
          position: 'absolute',
          bottom: 20,
          left: 36,
          right: 36,
          background: '#1a120b',
          borderRadius: 14,
          padding: '14px 22px',
          borderTop: '3px solid #92400e',
          opacity: quoteOpacity,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 22 }}>🪵</span>
          <span style={{ fontSize: 20, color: '#fef3c7', lineHeight: 1.6 }}>
            {p.bottomQuote}
          </span>
        </div>
      </div>

      {/* Progress bar */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: `${interpolate(frame, [0, 540], [0, 100], {
            extrapolateLeft: 'clamp',
            extrapolateRight: 'clamp',
          })}%`,
          height: 4,
          background: 'linear-gradient(90deg, #92400e, #d97706, #fbbf24)',
        }}
      />
    </AbsoluteFill>
  );
};
